import { stripePromise, getStripePriceId } from './stripe';
import { supabase } from './supabase';

// Interface for checkout options
export interface StripeCheckoutOptions {
  planId: string;
  businessId: string;
  customerEmail: string;
  successUrl: string;
  cancelUrl: string;
}

export type CreateStripeCheckoutSessionResult =
  | { success: true; planUpdated?: boolean; subscriptionId?: string }
  | { success: false; needsBillingPortal: true; message?: string };

// Create Stripe checkout session
export const createStripeCheckoutSession = async (
  options: StripeCheckoutOptions,
): Promise<CreateStripeCheckoutSessionResult> => {
  try {
    console.log('createStripeCheckoutSession called with options:', options);
    const { planId, businessId, customerEmail, successUrl, cancelUrl } = options;
    
    const priceId = await getStripePriceId(planId);
    console.log('Price ID for plan:', { planId, priceId });
    
    if (!priceId) {
      throw new Error(`No Stripe price ID found for plan: ${planId}`);
    }
    
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }
    
    console.log('Calling Edge Function with payload:', {
      action: 'create-checkout',
      priceId,
      businessId,
      customerEmail,
      successUrl,
      cancelUrl,
      planId
    });
    
    const { data, error } = await supabase.functions.invoke('stripe-subscription', {
      body: {
        action: 'create-checkout',
        priceId,
        businessId,
        customerEmail,
        successUrl,
        cancelUrl,
        planId
      }
    });
    
    console.log('Edge Function response:', { data, error });
    
    if (error) {
      console.error('Error creating checkout session:', error);
      throw new Error('Failed to create checkout session');
    }

    if (data?.success === false && data?.code === 'NEEDS_BILLING_PORTAL') {
      return {
        success: false,
        needsBillingPortal: true,
        message: typeof data.message === 'string' ? data.message : undefined,
      };
    }
    
    // Check if subscription was updated directly (no checkout needed)
    if (data?.success && data?.message === 'Plan updated successfully') {
      console.log('Plan updated successfully without checkout:', data);
      return { success: true, planUpdated: true, subscriptionId: data.subscriptionId };
    }
    
    if (!data?.sessionId) {
      console.error('No session ID returned:', data);
      throw new Error('No session ID returned from checkout creation');
    }
    
    console.log('Session ID received:', data.sessionId);
    
    const stripe = await stripePromise;
    if (!stripe) {
      throw new Error('Stripe not initialized');
    }
    
    console.log('Redirecting to Stripe checkout...');
    const { error: checkoutError } = await stripe.redirectToCheckout({ sessionId: data.sessionId });
    
    if (checkoutError) {
      console.error('Stripe checkout error:', checkoutError);
      throw new Error('Failed to redirect to checkout');
    }
    
    console.log('Successfully redirected to Stripe checkout');
    return { success: true };
  } catch (error) {
    console.error('Stripe checkout error:', error);
    throw error;
  }
};

export async function createPlatformBillingPortalSession(
  businessId: string,
  returnUrl: string,
): Promise<{ url: string } | null> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }
  const { data, error } = await supabase.functions.invoke('stripe-subscription', {
    body: {
      action: 'create-billing-portal',
      businessId,
      returnUrl,
    },
  });
  if (error) {
    console.error('create-billing-portal invoke error:', error);
    return null;
  }
  if (data?.url && typeof data.url === 'string') {
    return { url: data.url };
  }
  if (data?.error) {
    console.error('create-billing-portal:', data.error);
  }
  return null;
}

export type PayOpenInvoiceResult =
  | { paid: true }
  | { paid: false; noOpenInvoice: true }
  | { paid: false; noOpenInvoice?: false; message?: string; hostedInvoiceUrl?: string | null };

/**
 * Retry the open platform invoice with the card already on file (the Stripe
 * billing portal cannot charge open invoices, only update the card).
 */
export async function payOpenPlatformInvoice(businessId: string): Promise<PayOpenInvoiceResult> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }
  const { data, error } = await supabase.functions.invoke('stripe-subscription', {
    body: {
      action: 'pay-open-invoice',
      businessId,
    },
  });
  if (error) {
    console.error('pay-open-invoice invoke error:', error);
    throw new Error('Could not reach billing. Please try again.');
  }
  if (data?.paid === true) {
    return { paid: true };
  }
  if (data?.noOpenInvoice === true) {
    return { paid: false, noOpenInvoice: true };
  }
  return {
    paid: false,
    message: typeof data?.message === 'string' ? data.message : undefined,
    hostedInvoiceUrl: typeof data?.hostedInvoiceUrl === 'string' ? data.hostedInvoiceUrl : null,
  };
}

// Handle successful Stripe checkout
export const handleStripeCheckoutSuccess = async (sessionId: string) => {
  try {
    console.log('handleStripeCheckoutSuccess called with sessionId:', sessionId);
    
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }
    
    console.log('Calling Edge Function to verify session...');
    const { data, error } = await supabase.functions.invoke('stripe-subscription', {
      body: {
        action: 'verify-session',
        sessionId
      }
    });
    
    console.log('Session verification response:', { data, error });
    
    if (error) {
      console.error('Error verifying session:', error);
      throw new Error('Failed to verify checkout session');
    }
    
    console.log('Session verified successfully:', data);
    return data;
  } catch (error) {
    console.error('Session verification error:', error);
    throw error;
  }
}; 